import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import {
    FlatAssignmentResult,
    Column,
    FilterOption,
    ReportFilter,
} from '../../../shared/types/reports';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
    Control,
    FieldType,
    FilterField,
    FilterForm,
} from './assignments-filter.types';

@Component({
    selector: 'las-assignments-filter',
    templateUrl: './assignments-filter.component.html',
    styleUrls: ['./assignments-filter.component.scss'],
    standalone: false,
})
export class AssignmentsFilterComponent implements OnInit, OnChanges {
    readonly dateHint = 'MM/DD/YYYY';
    readonly filterColumns = [
        Column.Bill,
        Column.RequestType,
        Column.RequestStatus,
        Column.Priority,
        Column.Task,
        Column.TaskDue,
    ];

    @Input() assignments: FlatAssignmentResult[] = [];
    @Input() isOpen: boolean = false;
    @Output() onAssignmentsChanged = new EventEmitter<FlatAssignmentResult[]>();
    @Output() onFilterReset = new EventEmitter();

    options: Map<string, FilterOption[]>;
    fields: FilterField[];

    filters: FormGroup;
    assignmentFilters: ReportFilter[] = this.getUnmodifiedAssignmentFilters();

    constructor(private fb: FormBuilder) {
        this.fields = this.populateFilterFields();
        this.options = this.populateOptions();
        this.filters = FilterForm.build(this.fb);
    }

    ngOnInit() {
        this.filters.valueChanges.subscribe(() => this.applyFilters());
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['assignments'] && !changes['assignments'].firstChange) {
            this.applyFilters();
        }
    }

    resetFilters() {
        this.filters.get(Control.BillSearch)?.reset('');
        this.fields.forEach((field) => {
            field.controlName === Control.TaskDue
                ? [Control.TaskDueStart, Control.TaskDueEnd].forEach(
                      (dateField) => this.filters.get(dateField)?.reset(),
                  )
                : this.filters.get(field.controlName)?.reset(null);
        });
        this.onFilterReset.emit();
    }

    applyFilters() {
        const validFields = this.fields.filter((field) => {
            if (field.controlName === Control.TaskDue) {
                const start = this.filters.get(Control.TaskDueStart)?.value;
                const end = this.filters.get(Control.TaskDueEnd)?.value;
                return start || end;
            }
            const control = this.filters.get(field.controlName);
            const value = control?.value;
            return value && value !== '';
        });

        // Check if bill search has a value
        const billSearchValue = this.filters.get(Control.BillSearch)?.value;
        const hasBillSearch = billSearchValue && billSearchValue.trim() !== '';

        if (!validFields.length && !hasBillSearch) {
            this.onFilterReset.emit();
            return;
        }
        const filtered = this.assignments.filter((assignment) =>
            this.shouldInclude(assignment, validFields, billSearchValue),
        );
        this.onAssignmentsChanged.emit(filtered);
    }

    private shouldInclude(
        assignment: FlatAssignmentResult,
        filterFields: FilterField[],
        billSearchValue?: string,
    ) {
        // Check bill search text filter
        if (billSearchValue && billSearchValue.trim() !== '') {
            const searchText = billSearchValue.toLowerCase();
            const matchesMeasure = assignment.measure
                .toLowerCase()
                .includes(searchText);
            const matchesSubject = assignment.subject
                .toLowerCase()
                .includes(searchText);
            if (!matchesMeasure && !matchesSubject) return false;
        }

        // If no dropdown filters to check, include this assignment
        if (!filterFields.length) return true;

        // Check dropdown filters
        let keep = false;
        let compare = (values: any[], index: number): boolean => {
            const controlValue = this.filters.get(
                filterFields[index].controlName,
            )?.value;
            return values.some((value) => value === controlValue);
        };

        for (let i = 0; i < filterFields.length; ++i) {
            let assignmentValues: any[];
            switch (filterFields[i].label) {
                case Column.Bill:
                    assignmentValues = [assignment.measureType];
                    break;
                case Column.RequestType:
                    assignmentValues = [assignment.requestType];
                    break;
                case Column.RequestStatus:
                    assignmentValues = [assignment.requestStatus];
                    break;
                case Column.Priority:
                    assignmentValues = [assignment.requestPriority];
                    break;
                case Column.Task:
                    assignmentValues = [assignment.assignmentTask];
                    break;
                default:
                    // We treat dates as the default; the rest are select lists.
                    assignmentValues = assignment.assignmentDue
                        ? [assignment.assignmentDue]
                        : [];
                    compare = (dates: string[], __: number): boolean => {
                        const startDate = this.filters.get(
                            Control.TaskDueStart,
                        )?.value;
                        const endDate = this.filters.get(
                            Control.TaskDueEnd,
                        )?.value;
                        return dates.some((date: string) => {
                            const millis = Date.parse(date);
                            if (startDate && millis < startDate.getTime())
                                return false;
                            return !(endDate && endDate.getTime() < millis);
                        });
                    };
                    break;
            }
            keep = compare(assignmentValues, i);
            if (!keep) break;
        }
        return keep;
    }

    private populateOptions(): Map<string, FilterOption[]> {
        const map = new Map<string, FilterOption[]>();
        this.assignmentFilters.forEach((filter) => {
            if (
                [FieldType.SelectList, FieldType.MultiList].some(
                    (e) => e === filter.comparison,
                )
            ) {
                const name = this.getFormName(filter.name);
                map.set(name, filter.options!);
            }
        });
        return map;
    }

    private populateFilterFields(): FilterField[] {
        return this.filterColumns.map((filter) => ({
            label: filter,
            controlName: this.getFormName(filter),
            isSelectList: filter !== Column.TaskDue,
        }));
    }

    private getFormName(name: string): string {
        const parts = name.split(' ');
        const first = parts[0].toLowerCase();
        return `${first}${parts.length == 2 ? parts[1] : ''}`;
    }

    private getUnmodifiedAssignmentFilters() {
        return [
            {
                name: 'Bill',
                value: '',
                low: '',
                high: '',
                options: [
                    { name: 'AB' },
                    { name: 'ACA' },
                    { name: 'ACR' },
                    { name: 'AJR' },
                    { name: 'HR' },
                    { name: 'SB' },
                    { name: 'SCA' },
                    { name: 'SCR' },
                    { name: 'SJR' },
                    { name: 'SR' },
                ],
                comparison: 'selectlist',
            },
            {
                name: 'Request Type',
                value: '',
                low: '',
                high: '',
                options: [
                    { name: 'Program' },
                    { name: 'Short Form' },
                    { name: 'In-House' },
                    { name: 'Legal' },
                    { name: 'Fiscal Local' },
                    { name: 'Master' },
                    { name: 'Fiscal State' },
                    { name: 'EBR' },
                    { name: 'ISD' },
                ],
                comparison: 'selectlist',
            },
            {
                name: 'Request Status',
                value: '',
                low: '',
                high: '',
                options: [
                    { name: 'Analysis Required' },
                    { name: 'Track Only' },
                    { name: 'Complete' },
                    { name: 'Cancelled' },
                ],
                comparison: 'selectlist',
            },
            {
                name: 'Priority',
                value: '',
                low: '',
                high: '',
                options: [
                    { name: 'Low' },
                    { name: 'Normal' },
                    { name: 'High' },
                ],
                comparison: 'selectlist',
            },
            {
                name: 'Task',
                value: '',
                low: '',
                high: '',
                options: [
                    { name: 'Analyze' },
                    { name: 'Approve' },
                    { name: 'Coordinator' },
                ],
                comparison: 'selectlist',
            },
            {
                name: 'Task Due',
                value: '',
                low: '',
                high: '',
                path: 'vm.Billtypes',
                comparison: 'date',
            },
        ];
    }

    protected readonly Control = Control;
}
